<template>
  <div class="students-tab">
    <v-alert v-if="!members.length" type="info" text class="mb-0">
      {{ $t("studentsTab.empty") }}
    </v-alert>

    <v-row v-else dense>
      <v-col cols="12" md="4" lg="3">
        <div class="students-tab__list">
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            :label="$t('studentsTab.search')"
            outlined
            dense
            clearable
            hide-details
            class="mb-3"
          />

          <div class="students-tab__list-scroll">
            <button
              v-for="student in filteredMembers"
              :key="student.id"
              type="button"
              class="students-tab__item"
              :class="{
                'students-tab__item--active': student.id === selectedId,
              }"
              @click="selectStudent(student.id)"
            >
              <v-avatar
                size="40"
                class="mr-3"
                :color="student.avatar ? '' : theme.primary"
              >
                <v-img v-if="student.avatar" :src="student.avatar" cover />
                <span v-else class="white--text students-tab__initials">
                  {{ initials(student.name) }}
                </span>
              </v-avatar>

              <div class="students-tab__item-info">
                <div class="students-tab__item-name">
                  {{ student.name || $t("common.student") }}
                </div>
                <div class="students-tab__item-level">
                  {{ levelLabel(student.level) }}
                </div>
              </div>

              <v-chip x-small dark :color="statusColor(student.status)">
                {{ statusLabel(student.status) }}
              </v-chip>
            </button>

            <div v-if="!filteredMembers.length" class="students-tab__empty">
              {{ $t("studentsTab.notFound", { term: search }) }}
            </div>
          </div>
        </div>
      </v-col>

      <v-col cols="12" md="8" lg="9">
        <class-member-card
          v-if="selectedStudent"
          :key="selectedStudent.id"
          :student="selectedStudent"
          :group-students="members"
          :theme="theme"
          :normalized-times="normalizedTimes"
          :class-days="classDays"
          :level-options="levelOptions"
          :partner-plans="partnerPlans"
          show-actions
          show-billing
          show-billing-editor
          show-level-editor
          @reload="$emit('reload')"
        />

        <v-alert v-else type="info" text class="mb-0">
          {{ $t("studentsTab.select") }}
        </v-alert>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import ClassMemberCard from "@/components/classes/ClassMemberCard.vue";
import {
  ENGLISH_LEVELS,
  displayGroupLevel,
  initialsFromName,
  participantStatusColor,
  participantStatusLabel,
} from "@/shared/classPresentation";

export default {
  name: "ClassStudentsTab",

  components: {
    ClassMemberCard,
  },

  props: {
    members: {
      type: Array,
      default: () => [],
    },
    theme: {
      type: Object,
      required: true,
    },
    normalizedTimes: {
      type: Array,
      default: () => [],
    },
    classDays: {
      type: Array,
      default: () => [],
    },
    partnerPlans: {
      type: Array,
      default: () => [],
    },
    levelOptions: {
      type: Array,
      default: () => ENGLISH_LEVELS,
    },
  },

  data() {
    return {
      search: "",
      selectedId: null,
    };
  },

  computed: {
    filteredMembers() {
      const term = (this.search || "").trim().toLowerCase();

      if (!term) {
        return this.members;
      }

      return this.members.filter((member) =>
        String(member.name || "")
          .toLowerCase()
          .includes(term)
      );
    },
    selectedStudent() {
      return (
        this.members.find((member) => member.id === this.selectedId) ||
        this.members[0] ||
        null
      );
    },
  },

  watch: {
    members: {
      immediate: true,
      handler(members) {
        const exists = (members || []).some(
          (member) => member.id === this.selectedId
        );

        if (!exists) {
          this.selectedId = members && members.length ? members[0].id : null;
        }
      },
    },
  },

  methods: {
    selectStudent(id) {
      this.selectedId = id;
    },
    initials(name) {
      return initialsFromName(name);
    },
    levelLabel(level) {
      return displayGroupLevel(level);
    },
    statusLabel(status) {
      return participantStatusLabel(status);
    },
    statusColor(status) {
      return participantStatusColor(status);
    },
  },
};
</script>

<style scoped>
.students-tab__list {
  border: 1px solid rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  border-radius: 20px;
  padding: 14px;
  background: var(--dk-surface, #ffffff);
}

.students-tab__list-scroll {
  max-height: 520px;
  overflow-y: auto;
}

.students-tab__item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 12px;
  border: none;
  border-radius: 14px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.15s ease;
}

.students-tab__item:hover {
  background: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.04);
}

.students-tab__item:focus-visible {
  outline: 2px solid var(--dk-grape, #a826e1);
  outline-offset: -2px;
}

.students-tab__item--active {
  background: rgba(168, 38, 225, 0.1);
}

.students-tab__item-info {
  flex: 1;
  min-width: 0;
}

.students-tab__item-name {
  font-weight: 700;
  color: var(--dk-ink, #231a2e);
  font-size: 0.92rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.students-tab__item-level {
  font-size: 0.76rem;
  color: var(--dk-muted, #6b6472);
}

.students-tab__initials {
  font-size: 0.8rem;
  font-weight: 700;
}

.students-tab__empty {
  padding: 16px 12px;
  color: var(--dk-muted, #6b6472);
  font-size: 0.88rem;
}
</style>
